import { OFFER_CARGO_VEHICLE_SHAPES_LIST } from "@/shared/constants/offer-cargo";
import { BaseSelect } from "@/shared/ui/base-select";
import { MobileDeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table/mobile-delivery-proposal-form-table";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Typography,
} from "@mui/material";
import React from "react";
import { type Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DeliveryProposalFormLayout } from "../../../../../widgets/delivery-proposal-form-layout";
import { DeliveryProposalFormTable } from "../../../../../widgets/delivery-proposal-form-table";
import type { CarrierDeliveryProposalCreatePageProps } from "../../../model";
import type { CreateDeliveryProposalFormSchemaType } from "../../../model/validation";

export const VehicleForm = (props: {
  control: Control<CreateDeliveryProposalFormSchemaType>;
  draftOfferCargo: CarrierDeliveryProposalCreatePageProps["draftOfferCargo"];
  vehicles: CarrierDeliveryProposalCreatePageProps["vehicles"];
  defaultSelectedVehicleId: number | undefined;
}) => {
  const { t } = useTranslation();
  const { control, draftOfferCargo, vehicles, defaultSelectedVehicleId } =
    props;
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<number | "">(
    defaultSelectedVehicleId ?? "",
  );
  const selectedVehicle = vehicles.data?.find(
    (vehicle) => vehicle.id === selectedVehicleId,
  );

  const vehicleCells = [
    null,
    vehicles.data ? (
      <Controller
        key={"proposal"}
        name={"vehicleId"}
        control={control}
        defaultValue={selectedVehicleId === "" ? undefined : selectedVehicleId}
        render={({ field, fieldState }) => (
          <FormControl size="small" fullWidth error={fieldState.invalid}>
            <BaseSelect
              {...field}
              value={field.value}
              type="number"
              fullWidth
              noSelectedOptionText="(選択しない)"
              onChange={(value) => {
                field.onChange(value);
                if (value === "") {
                  return;
                }
                setSelectedVehicleId(value);
              }}
            >
              {vehicles.data?.map((vehicle) => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  <Typography variant="body1">
                    {vehicle.name} {vehicle.number}
                  </Typography>
                </MenuItem>
              ))}
            </BaseSelect>
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    ) : (
      <CircularProgress key="proposal" size={16} />
    ),
  ];

  const vehicleWeightCells = [
    draftOfferCargo.data?.vehicleWeight
      ? `${draftOfferCargo.data?.vehicleWeight}t`
      : null,
    selectedVehicle?.weight ? `${selectedVehicle.weight}t` : null,
  ];

  const vehicleShapeCells = [
    OFFER_CARGO_VEHICLE_SHAPES_LIST.find(
      (shape) => shape.value === draftOfferCargo.data?.vehicleShape,
    )?.label,
    (() => {
      const shapeText: string[] = [];
      if (selectedVehicle?.shapeText) {
        shapeText.push(selectedVehicle?.shapeText);
      }
      if (selectedVehicle?.equipmentsText) {
        shapeText.push(
          `装備品 : ${selectedVehicle?.equipmentsText.join(", ")}`,
        );
      }
      return shapeText.join("\n");
    })(),
  ];

  const vehicleEquipmentMemoCells = [
    draftOfferCargo.data?.vehicleEquipmentMemo,
    null,
  ];

  return (
    <DeliveryProposalFormLayout title="車両" isLoading={false}>
      <div>
        {/* PC用のフォーム */}
        <Box display={{ xs: "none", sm: "block" }}>
          <DeliveryProposalFormTable
            headers={["荷物", "提案"]}
            rows={[
              {
                title: "車両",
                cells: vehicleCells,
              },
              {
                title: t("offerCargo.vehicleWeight"),
                cells: vehicleWeightCells,
              },
              {
                title: t("offerCargo.vehicleShape"),
                cells: vehicleShapeCells,
              },
              {
                title: t("offerCargo.vehicleEquipmentMemo"),
                cells: vehicleEquipmentMemoCells,
              },
            ]}
          />
        </Box>

        {/* モバイル用のフォーム */}
        <Box display={{ xs: "block", sm: "none" }}>
          <MobileDeliveryProposalFormTable
            items={[
              {
                label: "車両",
                cargo: vehicleCells[0],
                proposal: vehicleCells[1],
              },
              {
                label: t("offerCargo.vehicleWeight"),
                cargo: vehicleWeightCells[0],
                proposal: vehicleWeightCells[1],
              },
              {
                label: t("offerCargo.vehicleShape"),
                cargo: vehicleShapeCells[0],
                proposal: vehicleShapeCells[1],
              },
              {
                label: t("offerCargo.vehicleEquipmentMemo"),
                cargo: vehicleEquipmentMemoCells[0],
                proposal: vehicleEquipmentMemoCells[1],
              },
            ]}
          />
        </Box>
      </div>
    </DeliveryProposalFormLayout>
  );
};
